// Chemin : C:\gestion-copro\src\components\chat\SentinelleAvatar.tsx
// Session 85 : Composant avatar SENTINELLE animé

import React from 'react';

interface SentinelleAvatarProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  isThinking?: boolean;
  className?: string;
}

const sizeMap = {
  sm: 32,
  md: 40,
  lg: 56,
  xl: 80,
};

export default function SentinelleAvatar({ 
  size = 'md', 
  isThinking = false,
  className = ''
}: SentinelleAvatarProps) {
  const dimension = sizeMap[size];
  
  return (
    <div 
      className={`relative inline-flex items-center justify-center ${className}`}
      style={{ width: dimension, height: dimension }}
    >
      {/* Effet glow externe */}
      <div 
        className={`absolute inset-0 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 blur-md opacity-40 ${
          isThinking ? 'animate-pulse' : ''
        }`}
      />
      
      {/* SVG Principal */}
      <svg
        width={dimension}
        height={dimension}
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="relative z-10"
      >
        {/* Définitions des dégradés */}
        <defs>
          {/* Dégradé principal du cercle */}
          <linearGradient id="sentinelleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3B82F6" />
            <stop offset="50%" stopColor="#1E40AF" />
            <stop offset="100%" stopColor="#4F46E5" />
          </linearGradient>
          
          {/* Dégradé pour les yeux */}
          <linearGradient id="eyeGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#FFFFFF" />
            <stop offset="100%" stopColor="#DBEAFE" />
          </linearGradient>
          
          {/* Glow pour les yeux */}
          <filter id="eyeGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          
          {/* Ombre interne */}
          <filter id="innerShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feOffset dx="0" dy="2"/>
            <feGaussianBlur stdDeviation="3" result="offset-blur"/>
            <feComposite operator="out" in="SourceGraphic" in2="offset-blur" result="inverse"/>
            <feFlood floodColor="#1E3A8A" floodOpacity="0.3" result="color"/>
            <feComposite operator="in" in="color" in2="inverse" result="shadow"/>
            <feComposite operator="over" in="shadow" in2="SourceGraphic"/>
          </filter>
        </defs>
        
        {/* Cercle de fond avec dégradé */}
        <circle 
          cx="50" 
          cy="50" 
          r="46" 
          fill="url(#sentinelleGradient)"
          filter="url(#innerShadow)"
        />
        
        {/* Anneau lumineux subtil */}
        <circle 
          cx="50" 
          cy="50" 
          r="43" 
          fill="none"
          stroke="rgba(255,255,255,0.2)"
          strokeWidth="1"
        />
        
        {/* Œil gauche */}
        <g filter="url(#eyeGlow)">
          <ellipse 
            cx="35" 
            cy="45" 
            rx="8" 
            ry={isThinking ? 10 : 12}
            fill="url(#eyeGradient)"
          />
          {/* Pupille gauche */}
          <ellipse 
            cx="35" 
            cy="47" 
            rx="3" 
            ry="4" 
            fill="#1E40AF"
            opacity="0.6"
          />
          {/* Reflet */}
          <ellipse 
            cx="33" 
            cy="42" 
            rx="2" 
            ry="2.5" 
            fill="white"
            opacity="0.8"
          />
        </g>
        
        {/* Œil droit */}
        <g filter="url(#eyeGlow)">
          <ellipse 
            cx="65" 
            cy="45" 
            rx="8" 
            ry={isThinking ? 10 : 12}
            fill="url(#eyeGradient)"
          />
          {/* Pupille droite */}
          <ellipse 
            cx="65" 
            cy="47" 
            rx="3" 
            ry="4" 
            fill="#1E40AF"
            opacity="0.6"
          />
          {/* Reflet */}
          <ellipse 
            cx="63" 
            cy="42" 
            rx="2" 
            ry="2.5" 
            fill="white"
            opacity="0.8"
          />
        </g>
        
        {/* Petit sourire subtil */}
        <path
          d="M 40 65 Q 50 72 60 65"
          fill="none"
          stroke="rgba(255,255,255,0.5)"
          strokeWidth="2"
          strokeLinecap="round"
        />
        
        {/* Indicateur de statut (petit point lumineux) */}
        <circle 
          cx="78" 
          cy="22" 
          r="6" 
          fill={isThinking ? '#F59E0B' : '#10B981'}
          className={isThinking ? 'animate-pulse' : ''}
        />
        <circle 
          cx="78" 
          cy="22" 
          r="3" 
          fill="white"
          opacity="0.6"
        />
      </svg>
    </div>
  );
}